import { Card } from '@/components/ui/card';
import { safeFormat } from '@/lib/safeDate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Brain, Target, AlertTriangle, MessageSquare, TrendingUp, Loader2, Sparkles, List, RefreshCw, Eye, Ear, Hand, Gauge, Users, Volume2, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { useContactInsights } from '@/hooks/useContactInsights';
import { useProcessCallInsights } from '@/hooks/useProcessCallInsights';
import { useProcessingJobsForCalls, CallInsightJob } from '@/hooks/useCallInsightJobs';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface ContactInsightsPanelProps {
  contactId: string;
}

// Tipos para os novos campos comportamentais
interface DiscProfile {
  dominant_trait: 'D' | 'I' | 'S' | 'C';
  d_score: number;
  i_score: number;
  s_score: number;
  c_score: number;
  analysis?: string;
}

interface VakProfile {
  primary: 'visual' | 'auditory' | 'kinesthetic';
  visual_score: number;
  auditory_score: number;
  kinesthetic_score: number;
  visual_words?: string[];
  auditory_words?: string[];
  kinesthetic_words?: string[];
  analysis?: string;
}

interface SpeechPatterns {
  speed: 'slow' | 'moderate' | 'fast';
  intonation: 'monotone' | 'varied' | 'very_varied';
  pauses: 'frequent' | 'few' | 'none';
  tone: 'formal' | 'casual' | 'emotional';
  top_words?: string[];
}

// Labels traduzidos
const DISC_LABELS: Record<string, { name: string; description: string; color: string }> = {
  D: { name: 'Dominância', description: 'Direto, decisivo, orientado a resultados', color: 'bg-red-500' },
  I: { name: 'Influência', description: 'Entusiasta, otimista, valoriza relacionamentos', color: 'bg-yellow-500' },
  S: { name: 'Estabilidade', description: 'Calmo, paciente, valoriza segurança', color: 'bg-green-500' },
  C: { name: 'Conformidade', description: 'Analítico, preciso, detalhista', color: 'bg-blue-500' },
};

const VAK_LABELS: Record<string, { name: string; icon: any; color: string }> = {
  visual: { name: 'Visual', icon: Eye, color: 'text-purple-500' },
  auditory: { name: 'Auditivo', icon: Ear, color: 'text-blue-500' },
  kinesthetic: { name: 'Cinestésico', icon: Hand, color: 'text-orange-500' },
};

const SPEED_LABELS: Record<string, string> = {
  slow: 'Lenta',
  moderate: 'Moderada',
  fast: 'Rápida',
};

const INTONATION_LABELS: Record<string, string> = {
  monotone: 'Monotônica',
  varied: 'Variada',
  very_varied: 'Muito Variada',
};

const TONE_LABELS: Record<string, string> = {
  formal: 'Formal',
  casual: 'Casual',
  emotional: 'Emocional',
};

// Componente para mostrar status do job
function JobStatusBadge({ job }: { job: CallInsightJob | undefined }) {
  if (!job) return null;
  
  switch (job.status) {
    case 'pending':
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
          <Clock className="w-3 h-3 mr-1" />
          Na fila
        </Badge>
      );
    case 'processing':
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 animate-pulse">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Processando...
        </Badge>
      );
    case 'completed':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Concluído
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
          <XCircle className="w-3 h-3 mr-1" />
          Falhou
        </Badge>
      );
    default:
      return null;
  }
}

export const ContactInsightsPanel = ({ contactId }: ContactInsightsPanelProps) => {
  const { insight, isLoading } = useContactInsights(contactId);
  const { processCallInsights, isProcessing } = useProcessCallInsights();
  const queryClient = useQueryClient();
  
  const [showCallsDrawer, setShowCallsDrawer] = useState(false);
  const [showProcessedDrawer, setShowProcessedDrawer] = useState(false);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [processingCallIds, setProcessingCallIds] = useState<Set<string>>(new Set());
  const [regeneratingCallIds, setRegeneratingCallIds] = useState<Set<string>>(new Set());

  // Cast dos novos campos
  const discProfile = insight?.disc_profile as DiscProfile | null;
  const vakProfile = insight?.vak_profile as VakProfile | null;
  const speechPatterns = insight?.speech_patterns as SpeechPatterns | null;

  // Query para buscar calls elegíveis para análise
  const { data: eligibleCalls, isLoading: callsLoading } = useQuery({
    queryKey: ['eligible-calls', contactId],
    queryFn: async () => {
      const { data: calls, error } = await supabase
        .from('calls')
        .select('id, started_at')
        .eq('contact_id', contactId)
        .eq('status', 'completed')
        .order('started_at', { ascending: false });

      if (error) throw error;
      if (!calls || calls.length === 0) return [];

      const callsWithCheck = await Promise.all(
        calls.map(async (call) => {
          const { data: recording } = await supabase
            .from('call_recordings')
            .select('id, duration_seconds')
            .eq('call_id', call.id)
            .maybeSingle();

          if (!recording || !recording.duration_seconds || recording.duration_seconds <= 60) {
            return null;
          }

          const { data: insights } = await supabase
            .from('call_insights')
            .select('id')
            .eq('call_id', call.id)
            .maybeSingle();

          return {
            id: call.id,
            started_at: call.started_at,
            duration_seconds: recording.duration_seconds,
            hasRecording: true,
            hasInsight: !!insights,
          };
        })
      );

      const eligible = callsWithCheck.filter(c => c !== null && !c.hasInsight);
      return eligible;
    },
    enabled: !!contactId,
  });

  // Query para buscar calls já processadas
  const { data: processedCalls, isLoading: processedLoading, refetch: refetchProcessedCalls } = useQuery({
    queryKey: ['processed-calls', contactId],
    queryFn: async () => {
      const { data: calls, error } = await supabase
        .from('calls')
        .select(`
          id, 
          started_at,
          call_insights!inner(id, summary, created_at)
        `)
        .eq('contact_id', contactId)
        .eq('status', 'completed')
        .order('started_at', { ascending: false });

      if (error) throw error;
      if (!calls || calls.length === 0) return [];

      const callsWithDuration = await Promise.all(
        calls.map(async (call: any) => {
          const insight = Array.isArray(call.call_insights) ? call.call_insights[0] : call.call_insights;

          const { data: recording } = await supabase
            .from('call_recordings')
            .select('duration_seconds')
            .eq('call_id', call.id)
            .maybeSingle();

          return {
            id: call.id,
            started_at: call.started_at,
            duration_seconds: recording?.duration_seconds || 0,
            insight_id: insight?.id as string | undefined,
            insight_created_at: insight?.created_at as string | undefined,
            summary: insight?.summary as string | undefined,
          };
        })
      );

      return callsWithDuration.filter(c => !!c.insight_id);
    },
    enabled: !!contactId,
  });

  // Coletar todos os call_ids para verificar jobs ativos
  const allCallIds = [
    ...(eligibleCalls?.map(c => c.id) || []),
    ...(processedCalls?.map(c => c.id) || []),
  ];

  // Hook para polling de jobs ativos
  const { data: activeJobs } = useProcessingJobsForCalls(allCallIds);

  // Efeito para atualizar queries quando jobs completam
  useEffect(() => {
    if (activeJobs) {
      const completedJobs = Object.values(activeJobs).filter(j => j.status === 'completed');
      if (completedJobs.length > 0) {
        // Refetch insights quando jobs completam
        queryClient.invalidateQueries({ queryKey: ['contact-insights', contactId] });
        queryClient.invalidateQueries({ queryKey: ['eligible-calls', contactId] });
        queryClient.invalidateQueries({ queryKey: ['processed-calls', contactId] });
      }
    }
  }, [activeJobs, contactId, queryClient]);

  const processedCallsCount = processedCalls?.length || 0;

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProcessSingleCall = async (callId: string) => {
    setProcessingCallIds(prev => new Set(prev).add(callId));
    
    try {
      await processCallInsights(callId);
      toast.success('Processamento iniciado em background!');
      
      // Invalidar jobs para iniciar polling
      queryClient.invalidateQueries({ queryKey: ['call-insight-jobs-batch'] });
    } catch (error) {
      console.error(`Erro ao processar call ${callId}:`, error);
      toast.error('Erro ao iniciar processamento');
    } finally {
      setProcessingCallIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(callId);
        return newSet;
      });
    }
  };

  const handleProcessAllCalls = async () => {
    if (!eligibleCalls || eligibleCalls.length === 0) return;

    setIsProcessingAll(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < eligibleCalls.length; i++) {
      const call = eligibleCalls[i];
      
      try {
        await processCallInsights(call.id);
        successCount++;
        
        // Delay entre calls para não sobrecarregar
        if (i < eligibleCalls.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error: any) {
        errorCount++;
        console.error(`Erro ao processar call ${call.id}:`, error);
        
        if (error.message?.includes('429') || error.message?.includes('402')) {
          toast.error('Processamento pausado devido a limites da API');
          break;
        }
      }
    }

    setIsProcessingAll(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} call(s) enviada(s) para processamento em background!`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} call(s) falharam ao iniciar processamento`);
    }
    
    // Invalidar jobs para iniciar polling
    queryClient.invalidateQueries({ queryKey: ['call-insight-jobs-batch'] });
  };

  const handleRegenerateInsights = async (callId: string, insightId: string) => {
    setRegeneratingCallIds(prev => new Set(prev).add(callId));
    
    try {
      const { error: deleteError } = await supabase
        .from('call_insights')
        .delete()
        .eq('id', insightId);

      if (deleteError) throw deleteError;

      await processCallInsights(callId);
      
      toast.success('Regeneração iniciada em background! Aguarde alguns instantes.');
      
      // Invalidar jobs para iniciar polling
      queryClient.invalidateQueries({ queryKey: ['call-insight-jobs-batch'] });
      queryClient.invalidateQueries({ queryKey: ['eligible-calls', contactId] });
    } catch (error: any) {
      console.error(`Erro ao regenerar insights da call ${callId}:`, error);
      toast.error(error.message || 'Erro ao regenerar insights');
    } finally {
      setRegeneratingCallIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(callId);
        return newSet;
      });
    }
  };

  const getSentimentColor = (level?: number) => {
    if (!level) return 'bg-muted';
    if (level >= 7) return 'bg-green-100 text-green-700';
    if (level >= 4) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  // Verificar se há jobs ativos para mostrar banner
  const hasActiveJobs = activeJobs && Object.values(activeJobs).some(j => 
    j.status === 'pending' || j.status === 'processing'
  );

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-32 mb-4" />
        <Skeleton className="h-20 w-full" />
      </Card>
    );
  }

  const totalCalls = (eligibleCalls?.length || 0) + (processedCallsCount || 0);
  const processedCount = processedCallsCount || 0;

  return (
    <div className="space-y-4">
      {/* Banner de Processamento em Background */}
      {hasActiveJobs && (
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">
                Processando áudios em background...
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {Object.values(activeJobs!).filter(j => j.status === 'processing').length} processando, 
                {' '}{Object.values(activeJobs!).filter(j => j.status === 'pending').length} na fila.
                A página será atualizada automaticamente.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Debug Info */}
      {callsLoading && (
        <Card className="p-4 bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
          <p className="text-sm">⏳ Carregando calls elegíveis...</p>
        </Card>
      )}

      {!callsLoading && (!eligibleCalls || eligibleCalls.length === 0) && processedCount === 0 && (
        <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <p className="text-sm">
            ℹ️ Nenhuma call elegível encontrada para este contato.
            <br/>
            <span className="text-xs text-muted-foreground">
              Critérios: duração &gt; 60s, status completed, com gravação, sem insights
            </span>
          </p>
        </Card>
      )}

      {/* Seção de Calls Pendentes */}
      {!callsLoading && eligibleCalls && eligibleCalls.length > 0 && (
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border-blue-200 dark:border-blue-800">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  {eligibleCalls.length} call{eligibleCalls.length > 1 ? 's' : ''} 
                  {eligibleCalls.length > 1 ? ' prontas' : ' pronta'} para análise
                </h3>
                <p className="text-sm text-muted-foreground">
                  Gere insights automaticamente com IA (processamento em background)
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCallsDrawer(true)}
                  disabled={isProcessingAll}
                >
                  <List className="w-4 h-4 mr-2" />
                  Ver Lista
                </Button>
                <Button
                  size="sm"
                  onClick={handleProcessAllCalls}
                  disabled={isProcessingAll || isProcessing}
                >
                  {isProcessingAll ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Processar Todas
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Indicador de Progresso */}
      {totalCalls > 0 && processedCount > 0 && (
        <Card className="p-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Análise de Calls</span>
              <span className="font-medium">{processedCount} de {totalCalls}</span>
            </div>
            <Progress value={(processedCount / totalCalls) * 100} className="h-2" />
          </div>
        </Card>
      )}

      {/* Seção de Calls Já Processadas - Regenerar */}
      {processedCalls && processedCalls.length > 0 && (
        <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1 flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-green-600" />
                  {processedCalls.length} call{processedCalls.length > 1 ? 's' : ''} já analisada{processedCalls.length > 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Regenere para usar transcrição real do áudio via Gemini
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProcessedDrawer(true)}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerar Insights
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Drawer com Lista de Calls Pendentes */}
      <Sheet open={showCallsDrawer} onOpenChange={setShowCallsDrawer}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Calls Pendentes de Análise</SheetTitle>
          </SheetHeader>
          <div className="space-y-3 mt-6">
            {eligibleCalls?.map((call) => {
              const activeJob = activeJobs?.[call.id];
              const isJobActive = activeJob?.status === 'pending' || activeJob?.status === 'processing';
              
              return (
                <Card key={call.id} className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <p className="font-medium">
                        {safeFormat(new Date(call.started_at), 'dd/MM/yyyy HH:mm')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Duração: {formatDuration(call.duration_seconds)}
                      </p>
                      {activeJob && (
                        <div className="mt-2">
                          <JobStatusBadge job={activeJob} />
                          {activeJob.status === 'failed' && activeJob.error_message && (
                            <p className="text-xs text-red-600 mt-1">{activeJob.error_message}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleProcessSingleCall(call.id)}
                      disabled={processingCallIds.has(call.id) || isProcessingAll || isJobActive}
                    >
                      {processingCallIds.has(call.id) || isJobActive ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          {isJobActive ? 'Processando' : 'Iniciando'}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Processar
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Drawer com Lista de Calls Já Processadas - Regenerar */}
      <Sheet open={showProcessedDrawer} onOpenChange={setShowProcessedDrawer}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Regenerar Insights de Calls</SheetTitle>
          </SheetHeader>
          <p className="text-sm text-muted-foreground mt-2 mb-4">
            Clique em "Regenerar" para reprocessar uma call com a nova transcrição real do áudio via Gemini AI.
            O processamento acontece em background.
          </p>
          <div className="space-y-3">
            {processedCalls?.map((call) => {
              const activeJob = activeJobs?.[call.id];
              const isJobActive = activeJob?.status === 'pending' || activeJob?.status === 'processing';
              
              return (
                <Card key={call.id} className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <p className="font-medium">
                        {safeFormat(new Date(call.started_at), 'dd/MM/yyyy HH:mm')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Duração: {formatDuration(call.duration_seconds)}
                      </p>
                      {call.insight_created_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Analisada em: {safeFormat(new Date(call.insight_created_at), 'dd/MM/yyyy HH:mm')}
                        </p>
                      )}
                      {activeJob && (
                        <div className="mt-2">
                          <JobStatusBadge job={activeJob} />
                          {activeJob.status === 'failed' && activeJob.error_message && (
                            <p className="text-xs text-red-600 mt-1">{activeJob.error_message}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => call.insight_id && handleRegenerateInsights(call.id, call.insight_id)}
                      disabled={regeneratingCallIds.has(call.id) || !call.insight_id || isJobActive}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      {regeneratingCallIds.has(call.id) || isJobActive ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          {isJobActive ? 'Processando' : 'Iniciando'}
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regenerar
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Mensagem quando não há insights */}
      {!insight && (!eligibleCalls || eligibleCalls.length === 0) && (
        <Card className="p-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Brain className="h-5 w-5" />
            <p>Nenhum insight de IA disponível ainda</p>
          </div>
        </Card>
      )}

      {/* Overall Summary */}
      {insight?.overall_summary && (
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <Brain className="h-5 w-5 text-primary mt-1" />
            <div>
              <h3 className="font-semibold mb-2">Resumo IA</h3>
              <p className="text-sm text-muted-foreground">{insight.overall_summary}</p>
            </div>
          </div>
        </Card>
      )}

      {/* ========== PERFIL DISC ========== */}
      {discProfile && (
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-indigo-500 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold mb-3">Perfil DISC</h3>
              
              {/* Traço dominante */}
              <div className="mb-4">
                <Badge className={`${DISC_LABELS[discProfile.dominant_trait]?.color} text-white text-lg px-3 py-1`}>
                  {discProfile.dominant_trait} - {DISC_LABELS[discProfile.dominant_trait]?.name}
                </Badge>
                <p className="text-sm text-muted-foreground mt-1">
                  {DISC_LABELS[discProfile.dominant_trait]?.description}
                </p>
              </div>

              {/* Barras de score */}
              <div className="space-y-3">
                {(['D', 'I', 'S', 'C'] as const).map((trait) => {
                  const score = discProfile[`${trait.toLowerCase()}_score` as keyof DiscProfile] as number;
                  const info = DISC_LABELS[trait];
                  return (
                    <div key={trait} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{trait} - {info.name}</span>
                        <span className="text-muted-foreground">{score}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${info.color} transition-all duration-500`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Análise */}
              {discProfile.analysis && (
                <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
                  <p className="text-sm">{discProfile.analysis}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ========== SISTEMA REPRESENTACIONAL VAK ========== */}
      {vakProfile && (
        <Card className="p-6">
          <div className="flex items-start gap-3">
            {(() => {
              const VakIcon = VAK_LABELS[vakProfile.primary]?.icon || Eye;
              return <VakIcon className={`h-5 w-5 ${VAK_LABELS[vakProfile.primary]?.color} mt-1`} />;
            })()}
            <div className="flex-1">
              <h3 className="font-semibold mb-3">Sistema Representacional (VAK)</h3>
              
              {/* Tipo dominante */}
              <div className="mb-4">
                <Badge variant="outline" className="text-lg px-3 py-1 border-2">
                  {VAK_LABELS[vakProfile.primary]?.name}
                </Badge>
                <p className="text-sm text-muted-foreground mt-1">
                  {vakProfile.primary === 'visual' && 'Processa informações através de imagens e visualizações'}
                  {vakProfile.primary === 'auditory' && 'Processa informações através de sons e palavras'}
                  {vakProfile.primary === 'kinesthetic' && 'Processa informações através de sensações e experiências'}
                </p>
              </div>

              {/* Scores */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                {(['visual', 'auditory', 'kinesthetic'] as const).map((type) => {
                  const score = vakProfile[`${type}_score` as keyof VakProfile] as number;
                  const info = VAK_LABELS[type];
                  const Icon = info.icon;
                  return (
                    <div key={type} className="text-center">
                      <Icon className={`h-6 w-6 mx-auto mb-1 ${info.color}`} />
                      <div className="text-2xl font-bold">{score}%</div>
                      <div className="text-xs text-muted-foreground">{info.name}</div>
                    </div>
                  );
                })}
              </div>

              {/* Palavras por tipo */}
              <div className="space-y-3">
                {vakProfile.visual_words && vakProfile.visual_words.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-purple-600 mb-1 flex items-center gap-1">
                      <Eye className="h-3 w-3" /> Palavras Visuais:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {vakProfile.visual_words.map((word, i) => (
                        <Badge key={i} variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                          {word}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {vakProfile.auditory_words && vakProfile.auditory_words.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-blue-600 mb-1 flex items-center gap-1">
                      <Ear className="h-3 w-3" /> Palavras Auditivas:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {vakProfile.auditory_words.map((word, i) => (
                        <Badge key={i} variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                          {word}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {vakProfile.kinesthetic_words && vakProfile.kinesthetic_words.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-orange-600 mb-1 flex items-center gap-1">
                      <Hand className="h-3 w-3" /> Palavras Cinestésicas:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {vakProfile.kinesthetic_words.map((word, i) => (
                        <Badge key={i} variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                          {word}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Análise */}
              {vakProfile.analysis && (
                <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                  <p className="text-sm">{vakProfile.analysis}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ========== PADRÕES DE FALA ========== */}
      {speechPatterns && (
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <Volume2 className="h-5 w-5 text-teal-500 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold mb-3">Padrões de Fala</h3>
              
              {/* Métricas em grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <Gauge className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                  <div className="text-sm font-medium">{SPEED_LABELS[speechPatterns.speed]}</div>
                  <div className="text-xs text-muted-foreground">Velocidade</div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <div className="h-4 w-4 mx-auto mb-1 text-muted-foreground">〰️</div>
                  <div className="text-sm font-medium">{INTONATION_LABELS[speechPatterns.intonation]}</div>
                  <div className="text-xs text-muted-foreground">Entonação</div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <div className="h-4 w-4 mx-auto mb-1 text-muted-foreground">⏸️</div>
                  <div className="text-sm font-medium">
                    {speechPatterns.pauses === 'frequent' ? 'Frequentes' : 
                     speechPatterns.pauses === 'few' ? 'Poucas' : 'Raras'}
                  </div>
                  <div className="text-xs text-muted-foreground">Pausas</div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <MessageSquare className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                  <div className="text-sm font-medium">{TONE_LABELS[speechPatterns.tone]}</div>
                  <div className="text-xs text-muted-foreground">Tom</div>
                </div>
              </div>

              {/* Palavras mais usadas */}
              {speechPatterns.top_words && speechPatterns.top_words.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Palavras mais usadas pelo cliente:</p>
                  <div className="flex flex-wrap gap-2">
                    {speechPatterns.top_words.map((word, i) => (
                      <Badge 
                        key={i} 
                        variant="outline" 
                        className={`${i < 3 ? 'text-base font-semibold' : 'text-sm'}`}
                      >
                        {word}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Goals & Pain Points */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {insight?.goals && insight.goals.length > 0 && (
          <Card className="p-6">
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-blue-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold mb-3">Objetivos</h3>
                <div className="flex flex-wrap gap-2">
                  {insight.goals.map((goal, i) => (
                    <Badge key={i} variant="outline" className="bg-blue-50">
                      {goal}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {insight?.pain_points && insight.pain_points.length > 0 && (
          <Card className="p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold mb-3">Pontos de Dor</h3>
                <div className="flex flex-wrap gap-2">
                  {insight.pain_points.map((pain, i) => (
                    <Badge key={i} variant="outline" className="bg-orange-50">
                      {pain}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Communication Style & Trust */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {insight?.communication_style && (
          <Card className="p-6">
            <div className="flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-purple-500 mt-1" />
              <div>
                <h3 className="font-semibold mb-2">Estilo de Comunicação</h3>
                <Badge className="bg-purple-50 text-purple-700 capitalize">
                  {insight.communication_style.replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>
          </Card>
        )}

        {insight?.trust_level !== undefined && (
          <Card className="p-6">
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-green-500 mt-1" />
              <div>
                <h3 className="font-semibold mb-2">Nível de Confiança</h3>
                <Badge className={getSentimentColor(insight.trust_level)}>
                  {insight.trust_level}/10
                </Badge>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Best Approach Notes */}
      {insight?.best_approach_notes && (
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Abordagem Recomendada
          </h3>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {insight.best_approach_notes}
          </p>
        </Card>
      )}

      {/* Risk Flags */}
      {insight?.risk_flags && insight.risk_flags.length > 0 && (
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold mb-3 text-red-700">Alertas de Risco</h3>
              <div className="flex flex-wrap gap-2">
                {insight.risk_flags.map((flag, i) => (
                  <Badge key={i} variant="destructive">
                    {flag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
